import { memo, useState, useCallback } from 'react'
import { StarIcon } from '@heroicons/react/24/solid'
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline'

/**
 * StarRating - Interactive or read-only 5-star rating
 * @param {number}   rating    Current rating value (1–5)
 * @param {Function} onChange  Called with new value (interactive mode only)
 * @param {'sm'|'md'|'lg'} size
 * @param {boolean}  readonly
 * @idempotent @atomic
 */
const StarRating = memo(function StarRating({
  rating = 0,
  onChange,
  size = 'md',
  readonly = false,
  className = ''
}) {
  const [hovered, setHovered] = useState(0)

  const sizeClasses = { sm: 'w-3 h-3', md: 'w-5 h-5', lg: 'w-6 h-6' }
  const iconSize = sizeClasses[size] || sizeClasses.md

  const handleSelect = useCallback((value) => {
    if (!readonly && onChange) onChange(value)
  }, [readonly, onChange])

  return (
    <div className={`flex items-center gap-0.5 ${className}`} role="group">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = (hovered || rating) >= star
        if (readonly) {
          return (
            <span key={star} aria-label={`${star} star${star !== 1 ? 's' : ''}`}>
              {filled
                ? <StarIcon className={`${iconSize} text-yellow-400`} />
                : <StarOutlineIcon className={`${iconSize} text-gray-300`} />
              }
            </span>
          )
        }
        return (
          <button
            key={star}
            type="button"
            className="cursor-pointer hover:scale-110 transition-transform focus:outline-none"
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => handleSelect(star)}
            onTouchEnd={(e) => { e.preventDefault(); handleSelect(star) }}
            aria-label={`${star} star${star !== 1 ? 's' : ''}`}
          >
            {filled
              ? <StarIcon className={`${iconSize} text-yellow-400`} />
              : <StarOutlineIcon className={`${iconSize} text-gray-300`} />
            }
          </button>
        )
      })}
    </div>
  )
})

export default StarRating
